var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { useGetTaskWidgetKPIs } from '@/shared/hooks/task/useGetTaskWidgetKPIs';
import { useDataContext } from '@/shared/providers/DataProvider';
import { useStateService } from '@/shared/services/StateService';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { MyTasksWidgetKPIOptions } from '../../../tasks/taskboard/constants';
import DashboardTaskKpiTile from '../partials/DashboardTaskKpiTile';
import messages from '../../dashboardMessages';
import { capacityPlanningOptions } from '@/modules/home/dashboards/MyTaskKpiDashboard';
var formatMoment = function (momentValue) { return momentValue.format('YYYY-MM-DD'); };
var getDeadlineFilter = function (kpi) {
    var now = moment.utc();
    if (kpi === MyTasksWidgetKPIOptions.pastDeadline) {
        return {
            deadlineFrom: null,
            deadlineUntil: formatMoment(now.clone().add(-1, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineOneWeek) {
        return {
            deadlineFrom: formatMoment(now.clone()),
            deadlineUntil: formatMoment(now.clone().add(7, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineTwoWeeks) {
        return {
            deadlineFrom: formatMoment(now.clone().add(7, 'days')),
            deadlineUntil: formatMoment(now.clone().add(14, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineThreeWeeks) {
        return {
            deadlineFrom: formatMoment(now.clone().add(14, 'days')),
            deadlineUntil: formatMoment(now.clone().add(21, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineFourWeeks) {
        return {
            deadlineFrom: formatMoment(now.clone().add(21, 'days')),
            deadlineUntil: formatMoment(now.clone().add(28, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineThreeMonths) {
        return {
            deadlineFrom: formatMoment(now.clone().add(28, 'days')),
            deadlineUntil: formatMoment(now.clone().add(3, 'months')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineSixMonths) {
        return {
            deadlineFrom: formatMoment(now.clone().add(3, 'months')),
            deadlineUntil: formatMoment(now.clone().add(6, 'months')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineOneYear) {
        return {
            deadlineFrom: formatMoment(now.clone().add(6, 'months')),
            deadlineUntil: formatMoment(now.clone().add(12, 'months')),
        };
    }
    return {};
};
export var MultipleTaskKpiTiles = function (_a) {
    var configuredKpis = _a.configuredKpis;
    var formatMessage = useIntl().formatMessage;
    var currentUserId = useDataContext().currentUserId;
    var _b = useGetTaskWidgetKPIs(configuredKpis), kpiData = _b.data, error = _b.error, isLoading = _b.isLoading, refetch = _b.refetch;
    useEffect(function () {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configuredKpis]);
    var _c = useMemo(function () {
        if (!configuredKpis)
            return [[], []];
        return configuredKpis
            .filter(function (x) { return x !== MyTasksWidgetKPIOptions.nextUserIsMe; })
            .reduce(function (result, x) {
            result[capacityPlanningOptions.indexOf(x) < 0 ? 0 : 1].push(x);
            return result;
        }, [[], []]);
    }, [configuredKpis]), deadlineKpis = _c[0], capacityKpis = _c[1];
    var stateService = useStateService();
    var getStartOfWeek = function (k) {
        switch (k) {
            case MyTasksWidgetKPIOptions.executionDateThisWeek:
                return moment().startOf('week');
            case MyTasksWidgetKPIOptions.executionDateNextWeek:
                return moment()
                    .startOf('week')
                    .add(7, 'days');
            case MyTasksWidgetKPIOptions.executionDateInTwoWeeks:
                return moment()
                    .startOf('week')
                    .add(14, 'days');
            case MyTasksWidgetKPIOptions.executionDateInThreeWeeks:
                return moment()
                    .startOf('week')
                    .add(21, 'days');
        }
    };
    var handleClick = function (k) {
        var _a;
        if (k === MyTasksWidgetKPIOptions.executionDateBeforeThisWeek) {
            stateService.gotoTaskList({
                userId: currentUserId,
                executionDate: {
                    from: null,
                    until: moment()
                        .startOf('week')
                        .format('YYYY-MM-DD'),
                },
                taskLevels: [1],
                mainStatus: [5],
                period: 'all',
            });
        }
        else if (capacityPlanningOptions.indexOf(k) >= 0) {
            stateService.gotoWeekPlanner({
                userId: currentUserId,
                executionDateFrom: (_a = getStartOfWeek(k)) === null || _a === void 0 ? void 0 : _a.format('YYYY-MM-DD'),
            });
        }
        else {
            stateService.gotoTaskboard(__assign({ userId: currentUserId, onlySubtasksForAssignedUser: true, userIsNextAssignedUser: configuredKpis.includes(MyTasksWidgetKPIOptions.nextUserIsMe) }, getDeadlineFilter(k)));
        }
    };
    return (_jsx("div", __assign({ className: "tasks-kpi-tiles" }, { children: _jsxs(LoadingContainer, __assign({ isLoading: isLoading }, { children: [!isLoading && error && _jsx("div", { children: formatMessage(messages.ErrorWhileRetrievingMyTaskKpis) }), !isLoading && !error && (_jsxs(_Fragment, { children: [deadlineKpis.length > 0 ? (_jsx("div", { children: _jsx("div", __assign({ className: "tile-grid" }, { children: deadlineKpis.map(function (x) {
                                    var _a;
                                    return (_jsx(DashboardTaskKpiTile, { value: (_a = kpiData === null || kpiData === void 0 ? void 0 : kpiData[x]) !== null && _a !== void 0 ? _a : 0, kpi: x, onClick: handleClick }, x));
                                }) })) })) : null, capacityKpis.length > 0 ? (_jsx("div", { children: _jsx("div", __assign({ className: "tile-grid" }, { children: capacityKpis.map(function (x) {
                                    var _a;
                                    return (_jsx(DashboardTaskKpiTile, { value: (_a = kpiData === null || kpiData === void 0 ? void 0 : kpiData[x]) !== null && _a !== void 0 ? _a : 0, kpi: x, onClick: handleClick }, x));
                                }) })) })) : null] }))] })) })));
};
